import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { checkAndRecordRateLimit, getClientIpHash } from "@/lib/request-security";

const Input = z.object({
  texts: z.array(z.string().max(2000)).max(200),
  target: z.enum(["en", "pt"]),
});

const GATEWAY_URL = "https://ai.gateway.lovable.dev/v1/chat/completions";

const LANG_NAMES = { en: "English", pt: "Portuguese (Brazil)" } as const;

export const translateBatch = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Input.parse(d))
  .handler(async ({ data }) => {
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("Missing LOVABLE_API_KEY");
    if (data.texts.length === 0) return { translations: [] as string[] };

    // Per-IP rate limit against paid AI gateway abuse.
    // Allow up to 30 batches per 10 min window per IP (covers legitimate page
    // navigation + language switches while stopping scripted abuse).
    try {
      const request = getRequest();
      if (request) {
        const ipHash = getClientIpHash(request);
        const rl = await checkAndRecordRateLimit({
          formKey: "translate-batch",
          ipHash,
          windowMinutes: 10,
          max: 30,
        });
        if (!rl.ok) {
          throw new Error("rate_limited");
        }
      }
    } catch (e) {
      if (e instanceof Error && e.message === "rate_limited") throw e;
      // Ignore rate-limit infra failures — fail open rather than blocking UI.
    }

    const langName = LANG_NAMES[data.target];
    const numbered = data.texts.map((t, i) => `${i}. ${t.replace(/\n/g, " ")}`).join("\n");

    const res = await fetch(GATEWAY_URL, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "Lovable-API-Key": apiKey,
      },
      body: JSON.stringify({
        model: "google/gemini-3-flash-preview",
        messages: [
          {
            role: "system",
            content:
              `Translate each numbered line from Spanish to ${langName}. ` +
              `Preserve brand names (Mine Class, MineClass, SonIA, FaceID, Mine Pass, Codelco, OTEC), ` +
              `numbers, emails, URLs. Keep same tone and casing style. ` +
              `Return STRICT JSON: {"items":[{"i":<index>,"t":"<translation>"}, ...]} with one entry per input line, in order. No prose.`,
          },
          { role: "user", content: numbered },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`AI gateway ${res.status}: ${body.slice(0, 200)}`);
    }
    const json = (await res.json()) as {
      choices?: { message?: { content?: string } }[];
    };
    const content = json.choices?.[0]?.message?.content ?? "{}";
    let parsed: { items?: { i: number; t: string }[] } = {};
    try {
      parsed = JSON.parse(content);
    } catch {
      parsed = {};
    }
    const out = new Array<string>(data.texts.length).fill("");
    for (const it of parsed.items ?? []) {
      if (typeof it.i === "number" && it.i >= 0 && it.i < out.length && typeof it.t === "string") {
        out[it.i] = it.t;
      }
    }
    // Fallback: any missing entry -> original
    for (let i = 0; i < out.length; i++) if (!out[i]) out[i] = data.texts[i];
    return { translations: out };
  });
